import { Injectable } from '@angular/core';
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Store } from '@ngxs/store';
import { Logout } from '@core/states/auth.state';

/**
 * @title HttpErrorInterceptor
 * @since 1.0.0
 * 
 * @description The `HttpErrorInterceptor` is an Angular HTTP interceptor that handles 
 * HTTP errors globally. It intercepts HTTP requests and responses, allowing you to handle 
 * errors in a centralized manner. This interceptor is particularly useful for displaying 
 * error messages to the user and managing authentication-related errors.
 */
@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {

    /**
     * The last error message displayed to the user.
     * @type {string}
     */
    private lastErrorMessage: string = '';

    /**
     * The timestamp of the last error message displayed.
     * @type {number}
     */
    private lastErrorTime: number = 0;

    /**
     * The interval (in milliseconds) for displaying error messages.
     * @type {number}
     */
    private errorDisplayInterval: number = 5000; // 5 seconds

    /**
     * @param {MessageService} messageService - The PrimeNG message service for displaying messages.
     * @param {Store} store - The NGXS store for managing application state.
     * @param {Router} router - The Angular router for navigation.
     */
    constructor(
        private messageService: MessageService,
        private store: Store, 
        private router: Router
    ) {}

    /**
     * Intercepts HTTP requests and handles errors.
     *
     * @param req - The outgoing HTTP request.
     * @param next - The next interceptor in the chain.
     * @returns An observable of the HTTP event.
     *
     * This interceptor catches HTTP errors and processes them based on their status codes:
     * - If the error is a client-side error (instance of `ErrorEvent`), it extracts the error message.
     * - If the error is a server-side error, it handles specific status codes:
     *   - `0`: No connection to the server or API.
     *   - `504`: Gateway timeout.
     *   - `401`: Unauthorized access, triggers a logout and redirects to the login page.
     *   - Other status codes: Re-throws the error with the server-provided message.
     *
     */
    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(req).pipe(
            catchError((error: HttpErrorResponse) => {
                let errorMessage = '';

                if (error.error instanceof ErrorEvent) {
                    errorMessage = `${error.error.message}`;
                } else {
                    switch (error.status) {
                        case 0:
                            errorMessage = 'Keine Verbindung zum Server oder zur API möglich. Bitte versuchen Sie es später erneut.';
                            break;
                        case 504:
                            errorMessage = 'Gateway-Timeout. Bitte versuchen Sie es später erneut.';
                            break;
                        case 401:
                            errorMessage = 'Sie sind nicht berechtigt, diese Aktion auszuführen.';
                            this.store.dispatch(new Logout()).subscribe(() => {
                                this.router.navigate(['/login']);
                            });
                            break;
                        default:
                            return throwError(() => new Error(error.error.message || error.message));
                    }
                }

                /*    
                * It ensures that error messages are not displayed repeatedly within a short interval.
                * If a new error message is different from the last one or enough time has passed since 
                * the last error, it displays the error message using the `messageService`.
                */
                const currentTime = new Date().getTime();
                if (errorMessage !== this.lastErrorMessage || (currentTime - this.lastErrorTime) > this.errorDisplayInterval) {
                    this.messageService.add({severity: 'error', summary: 'Fehler', detail: errorMessage});
                    this.lastErrorMessage = errorMessage;
                    this.lastErrorTime = currentTime;
                }

                return throwError(() => new Error(errorMessage));
            })
        );
    }
}